import { HttpClient } from '@angular/common/http'
import { inject, Injectable, InjectionToken, signal, WritableSignal } from '@angular/core'
import { lastValueFrom } from 'rxjs'
import { ENV_TOKEN } from '../../app.config'
import { EmployeeService } from '../../services/employee.service'
import { stilleAbfrage } from '../../util/versorgung-poll'
import { Herkunft } from './herkunft'

/**
 * Woher `app-mitarbeiter-avatar` das Logo eines Hauses oder Partners bekommt. Ein Token aus
 * demselben Grund wie `PROFILBILD_QUELLE`: Zeilen und Harness rendern ohne HttpClient, und ohne
 * Quelle bleiben die Initialen stehen.
 */
export interface HerkunftsQuelle {
  /** Bild-URL oder `null`. Liest ein Signal: der Avatar zeichnet nach, sobald das Logo da ist. */
  logo(herkunft: Herkunft): string | null
}

export const HERKUNFT_QUELLE = new InjectionToken<HerkunftsQuelle>('HERKUNFT_QUELLE')

/**
 * Lädt die vorhandenen Logos EINMAL je Seitenaufruf und Herkunft, beim ersten Avatar, der eins
 * braucht: das des Hauses (`staticdata/logo`, leerer Rumpf = keins) und das eines Partners
 * (`leistungserbringer/externalPartners/{id}/logo`, 204 = keins). Als Blob, weil die Endpunkte den
 * Token brauchen, dasselbe Muster wie `ProfilbildService`. Bewusst kein eigener Backend-Abruf: das
 * Logo wird unverändert in den Kreis eingepasst (Backend `docs/claude/nutzerprofil.md`).
 *
 * Still, weil es nur eine Zugabe ist: scheitert ein Abruf, stehen Initialen wie bisher und die
 * Fehlerzentrale schweigt. Ein Partnerlogo darf nur `LEISTUNGSERBRINGER` lesen (Agenten
 * eingeschlossen); fuer einen externen Partner wird es deshalb gar nicht erst abgefragt. Seit die
 * Partnerlogos auch in Listen stehen (`zeichenFuerListen`), kaeme sonst auf seiner Startseite je
 * Partner ein 403 an, der in Application Insights wie ein Vorfall aussieht.
 */
@Injectable({ providedIn: 'root' })
export class HerkunftsZeichenService implements HerkunftsQuelle {
  private readonly http = inject(HttpClient)
  private readonly apiUrl = inject(ENV_TOKEN).api.leistungserbringerUrl
  private readonly employeeService = inject(EmployeeService)
  private readonly logos = new Map<string, WritableSignal<string | null>>()

  logo(herkunft: Herkunft): string | null {
    const pfad =
      herkunft.art === 'haus'
        ? 'staticdata/logo'
        : herkunft.art === 'partner' && herkunft.partnerId && this.employeeService.hasRoleLeistungserbringer()
          ? `leistungserbringer/externalPartners/${encodeURIComponent(herkunft.partnerId)}/logo`
          : null
    if (!pfad) return null
    let url = this.logos.get(pfad)
    if (!url) {
      url = signal<string | null>(null)
      this.logos.set(pfad, url)
      void this.laden(pfad, url)
    }
    return url()
  }

  private async laden(pfad: string, ziel: WritableSignal<string | null>): Promise<void> {
    try {
      const bild = await lastValueFrom(this.http.get(`${this.apiUrl}/${pfad}`, { responseType: 'blob', context: stilleAbfrage() }))
      if (bild.size > 0) ziel.set(URL.createObjectURL(bild))
    } catch {
      // Initialen statt Logo; siehe Kommentar an der Klasse.
    }
  }
}
